import { AdsConfigService } from './ads_config.js';

const STORAGE_KEY_PREFIX = 'rewarded_booster_last_granted_at';
const MILLISECONDS_PER_MINUTE = 60_000;

export type RewardedCooldownId =
  | 'firework'
  | 'tenballs'
  | 'infiniteballs'
  | 'bigball'
  | 'hearts'
  | 'mysterybox';

function storageKey( cooldown_id: RewardedCooldownId ): string
{
  let player_id = 'guest';
  try
  {
    player_id = localStorage.getItem( 'player_id' ) ?? player_id;
  }
  catch
  {
    // Use the guest namespace when persistent storage is unavailable.
  }
  return `${STORAGE_KEY_PREFIX}:${player_id}:${cooldown_id}`;
}

function loadLastGrantedAt( cooldown_id: RewardedCooldownId ): number
{
  try
  {
    const value = Number( localStorage.getItem( storageKey( cooldown_id ) ) );
    return Number.isFinite( value ) && value > 0 ? value : 0;
  }
  catch
  {
    return 0;
  }
}

export function rewardedCooldownDurationMs( cooldown_id: RewardedCooldownId ): number
{
  const config = AdsConfigService.get().rewarded_break;
  if ( !config.enabled ) return 0;

  const countdown_minutes = {
    firework: config.firework_countdown,
    tenballs: config.tenballs_countdown,
    infiniteballs: config.infiniteballs_countdown,
    bigball: config.bigball_countdown,
    hearts: config.hearts_countdown,
    mysterybox: config.mysterybox_countdown,
  }[cooldown_id];
  return countdown_minutes * MILLISECONDS_PER_MINUTE;
}

export function rewardedCooldownRemainingMs(
  cooldown_id: RewardedCooldownId,
  now = Date.now()
): number
{
  const duration = rewardedCooldownDurationMs( cooldown_id );
  if ( duration <= 0 ) return 0;

  const remaining = loadLastGrantedAt( cooldown_id ) + duration - now;
  return Math.max( 0, Math.min( duration, remaining ) );
}

export function isRewardedCooldownActive(
  cooldown_id: RewardedCooldownId,
  now = Date.now()
): boolean
{
  return rewardedCooldownRemainingMs( cooldown_id, now ) > 0;
}

export function startRewardedCooldown(
  cooldown_id: RewardedCooldownId,
  now = Date.now()
): void
{
  const key = storageKey( cooldown_id );
  try
  {
    localStorage.setItem( key, String( now ) );
    console.log( '[RewardedCooldown] started', {
      cooldown_id,
      duration_ms: rewardedCooldownDurationMs( cooldown_id ),
      started_at: now,
      storage_key: key,
    } );
  }
  catch
  {
    console.warn( '[RewardedCooldown] could not persist cooldown' );
  }
}

export function rewardedCooldownTimerText( remaining_ms: number ): string
{
  const total_seconds = Math.max( 0, Math.ceil( remaining_ms / 1000 ) );
  const hours = Math.floor( total_seconds / 3600 );
  const minutes = Math.floor( total_seconds % 3600 / 60 );
  const seconds = total_seconds % 60;
  const pad = ( value: number ): string => String( value ).padStart( 2, '0' );
  return `${pad( hours )}:${pad( minutes )}:${pad( seconds )}`;
}
